import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers";

interface DueDatePickerProps {
  value: string | null | undefined;
  onChange: (dateString: string | undefined) => void;
  error?: string;
  disabled?: boolean;
  open?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
}

const validDueDate = (
  dueDate: string | null | undefined,
  ignorePast: boolean = false,
): null | Date => {
  if (!dueDate) {
    return null;
  }
  const regex = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
  if (!regex.test(dueDate)) {
    return null;
  }
  const date = new Date(dueDate);
  if (!ignorePast && date < new Date()) {
    return null;
  }
  return date;
};

const DueDatePicker = ({
  value,
  onChange,
  error,
  disabled = false,
  open: externalOpen,
  onClose: externalOnClose,
  onOpen,
}: DueDatePickerProps) => {
  const [internalOpen, setInternalOpen] = useState(false);

  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const handleClose = () => {
    if (externalOnClose) {
      externalOnClose();
    } else {
      setInternalOpen(false);
    }
  };
  const handleOpen = () => {
    if (onOpen) {
      onOpen();
    } else {
      setInternalOpen(true);
    }
  };

  return (
    <DatePicker
      disabled={disabled}
      sx={(theme) => ({
        ".MuiOutlinedInput-input": {
          margin: "0px",
          padding: "0px",
          marginLeft: "20px",
          color: theme.palette.text.primary,
          fontSize: "14px",
          fontWeight: "500",
          marginBottom: "15px",
        },
        ".MuiInputAdornment-root": {
          padding: "2px 16px 0px 0px !important",
          ".MuiButtonBase-root": {
            padding: "0px",
          },
        },
        svg: {
          margin: "0px",
          marginBottom: "18px",
        },
        width: "100%",
        height: "40px",
        minHeight: "40px",
        div: {
          padding: "8px",
          fieldset: {
            border: "1px solid #D4D4D4",
            borderRadius: "100px",
            height: "40px",
            minHeight: "40px",
            color: "#8E9598",
            fontSize: "14px",
            fontWeight: "500",
          },
        },
      })}
      slots={{
        openPickerIcon: () => (
          <ChevronDown size={20} strokeWidth="2px" color="#8E9598" />
        ),
      }}
      onChange={(v: Date | null) =>
        onChange(v && !isNaN(v.getTime()) ? v.toLocaleDateString() : undefined)
      }
      open={isOpen}
      value={validDueDate(value, true) || null}
      onClose={handleClose}
      slotProps={{
        textField: {
          color: "primary",
          error: !!error,
          helperText: error,
          onClick: handleOpen,
          placeholder: "Due date",
        },
      }}
    />
  );
};

export default DueDatePicker;
